// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { RxDatabase } from 'rxdb'
import { QueryOperator } from '../operators/QueryOperator'
import { MutationOperator } from '../operators/MutationOperator'
import type { QueryOptions } from '../operators/types'
import type { RoleModel } from '../../models/Role'
import { createId } from '../../index'

/**
 * Filters for querying roles.
 */
export interface RoleFilters {
  /** Filter by owning organisation ID. */
  orgId?: string
  /** Filter by role slug (e.g. `'owner'`, `'viewer'`). */
  name?: string
  /** Filter by built-in flag. */
  builtIn?: boolean
  /** When true, include archived roles only. When absent, exclude archived roles. */
  archived?: boolean
  /** Filter by scope type (e.g. `'workspace'`, `'project'`). */
  scopeType?: string
  /** Filter by scope entity ID. */
  scopeId?: string
}

/**
 * Input for creating a new role.
 */
export interface CreateRoleInput {
  /** Role slug used in permission checks. */
  name: string
  /** Human-readable display label. */
  title: string
  /** Optional description of what this role grants. */
  description?: string
  /** ID of the owning organisation. */
  orgId: string
  /** Scope type for workspace/project-scoped roles. */
  scopeType?: string
  /** Scope entity ID (required when scopeType is set). */
  scopeId?: string
  /** Whether this is a system-seeded built-in role. Defaults to false. */
  builtIn?: boolean
}

/**
 * Input for updating an existing role.
 */
export interface UpdateRoleInput {
  /** ID of the role to update. */
  id: string
  /** Fields to update. */
  updates: Partial<RoleModel>
}

/**
 * Operator for querying and mutating the `roles` collection.
 *
 * Follows the operator pattern established by `UserOperator` and `OrgOperator`.
 * All mutations are soft operations — deletion archives rather than removes.
 *
 * @example
 * ```typescript
 * const op = new RoleOperator(db)
 * const roles = await op.query({ orgId: 'org_abc', builtIn: true }).exec()
 * const role = await op.createMutation().execute({ name: 'viewer', title: 'Viewer', orgId: 'org_abc' })
 * ```
 */
export class RoleOperator {
  constructor(private db: RxDatabase) {}

  /**
   * Build a query for roles with optional filters and pagination.
   *
   * Excludes archived roles by default. Pass `archived: true` to query
   * only archived roles.
   *
   * @param filters - Field filters to apply.
   * @param options - Pagination and sort options.
   * @returns A `QueryOperator` ready to `.exec()` or `.observable`.
   */
  query(
    filters: RoleFilters,
    options?: { sort?: string; limit?: number; skip?: number }
  ): QueryOperator<RoleModel> {
    const collection = this.db.collections.roles

    // Use $ne: true rather than $eq: false so that docs where archived is
    // undefined or missing are also included in the non-archived result set.
    const selector: Record<string, unknown> = {
      archived: filters.archived === true ? { $eq: true } : { $ne: true },
    }

    if (filters.orgId) selector.orgId = { $eq: filters.orgId }
    if (filters.name) selector.name = { $eq: filters.name }
    if (filters.builtIn !== undefined) selector.builtIn = { $eq: filters.builtIn }
    if (filters.scopeType) selector.scopeType = { $eq: filters.scopeType }
    if (filters.scopeId) selector.scopeId = { $eq: filters.scopeId }

    const queryOptions: QueryOptions<RoleModel> = {
      selector,
      sort: options?.sort ? [{ [options.sort]: 'desc' }] : [{ createdAt: 'desc' }],
      limit: options?.limit,
      skip: options?.skip,
    }

    return new QueryOperator<RoleModel>(collection, queryOptions)
  }

  /**
   * Fetch a single role by its ID.
   *
   * @param roleId - The role document ID.
   * @returns The role as a plain object, or null if not found.
   */
  async findOne(roleId: string): Promise<RoleModel | null> {
    const doc = await this.db.collections.roles.findOne(roleId).exec()
    return doc ? (doc.toJSON() as RoleModel) : null
  }

  /**
   * Create a mutation operator for inserting a new role.
   *
   * Assigns a generated ID and sets `archived: false` and timestamps
   * automatically.
   *
   * @returns A `MutationOperator` whose `execute()` resolves to the new role.
   */
  createMutation(): MutationOperator<RoleModel, CreateRoleInput> {
    return new MutationOperator<RoleModel, CreateRoleInput>(this.db.collections.roles, {
      mutationFn: async (collection, input) => {
        const now = new Date().toISOString()
        const role: RoleModel = {
          id: createId('roles'),
          name: input.name,
          title: input.title,
          orgId: input.orgId,
          builtIn: input.builtIn ?? false,
          archived: false,
          scopeType: input.scopeType ?? '',
          scopeId: input.scopeId ?? '',
          description: input.description ?? '',
          createdAt: now,
          updatedAt: now,
        }
        await collection.insert(role)
        return role
      },
    })
  }

  /**
   * Create a mutation operator for updating role fields.
   *
   * Always stamps `updatedAt` on the modified document.
   *
   * @returns A `MutationOperator` whose `execute()` resolves to the updated role.
   */
  updateMutation(): MutationOperator<RoleModel, UpdateRoleInput> {
    return new MutationOperator<RoleModel, UpdateRoleInput>(this.db.collections.roles, {
      mutationFn: async (collection, { id, updates }) => {
        const doc = await collection.findOne(id).exec()
        if (!doc) throw new Error(`Role ${id} not found`)
        await doc.update({ $set: { ...updates, updatedAt: new Date().toISOString() } })
        return doc.toJSON() as RoleModel
      },
    })
  }

  /**
   * Create a mutation operator for soft-deleting a role.
   *
   * Sets `archived: true` rather than removing the document so that
   * audit trails and existing membership references remain intact.
   *
   * @returns A `MutationOperator` whose `execute()` takes the role ID.
   */
  deleteMutation(): MutationOperator<void, string> {
    return new MutationOperator<void, string>(this.db.collections.roles, {
      mutationFn: async (collection, roleId) => {
        const doc = await collection.findOne(roleId).exec()
        if (!doc) throw new Error(`Role ${roleId} not found`)
        await doc.update({ $set: { archived: true, updatedAt: new Date().toISOString() } })
      },
    })
  }
}
